import React, { useContext, useState } from 'react';
import {
  IFilledLocalAnalys,
  ILocalAnalys,
} from '../EkonomiskAnalysMain/EkonomiskAnalysMain';
import {
  ISelectedAnalysCodePrivilege,
  ISelectedAnalys,
} from '../../EkonomiskAnalysPage';
import EkonomiskAnalysQuery, {
  IFieldInfo,
  IRow,
} from '../../Queries/EkonomiskAnalysQuery/EkonomiskAnalysQuery';
import { tryConvertUIUrvalToTEDUrval } from '../../../../../../../Components/Urval/Utils/convertUIUrvalToTEDUrval';
import EkonomiskAnalysRadBaseradQuery, {
  IRow as IRadBaseradRow,
} from '../../Queries/EkonomiskAnalysRadBaseradQuery/EkonomiskAnalysRadBaseradQuery';
import { Theme } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import EkonomiskAnalysDetailInfo from '../AnalysDetailInfo/EkonomiskAnalysDetailInfo';
import EkonomiskAnalysDataErrorPage from '../ErrorPages/EkonomiskAnalysDataErrorPage/EkonomiskAnalysDataErrorPage';
import EkonomiskAnalysTable from './EkonomiskAnalysTable';
import EDTablePagination from '../../../../../../../BaseComponents/Table/Components/EDTablePagination';
import { EkonomiskAnalysContext } from '../Context/EkonomiskAnalysContext';
import {
  UNCHOSEN_ROWS_PER_PAGE,
  ROWS_PER_PAGE_CHOICES,
} from '../EkonomiskAnalysDetailTable/EkonomiskAnalysDetailTable';
import { AnvandarInfo } from '../../Const/AnvandarInfo';
import shortid from 'shortid';
import EkonomiskAnalysSaveDBSettingMutation, {
  EkonomiskAnalysSaveDBSettingType,
} from '../../Queries/EkonomiskAnalysSaveDBSettingMutation/EkonomiskAnalysSaveDBSettingMutation';
import { getForceHelarsJamforelse } from '../../Utils/userInfoUtils';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';

interface IProps {
  lopandeAnalys: boolean;
  settingsForCurrentlyShown: IFilledLocalAnalys;
  impersonatedCodePrivilege?: ISelectedAnalysCodePrivilege;
  selectedAnalys: ISelectedAnalys;
  settingsChanged: boolean;
  updateImpersonatedCodePrivilege: (
    codePrivilege?: ISelectedAnalysCodePrivilege
  ) => void;
  onDenominationChanged: (denomination: number) => void;
  expandedSumIndex: number;
  // scrollParentRef: any;
  handleScroll: (event: React.UIEvent) => void;
  updatedSettings: ILocalAnalys;
  userInfoLoading: boolean;
  setRefetchAnalys: (refetchAnalys: () => void) => void;
  stopTableRendering: boolean;
  classes?: any;
  sammanfattningsMode?: boolean;
}

interface IInnerProps extends IProps {
  showTable: boolean;
  showErrorPage: boolean;
  columns: IColumn[];
  lopandeVariables?: ILopandeVariables;
  radbaseradVariables?: IRadbaseradVariables;
  saveDBSetting?: EkonomiskAnalysSaveDBSettingType;
}

export interface ILopandeVariables {
  rows: IRow[];
  totalSumRow?: IRow;
  expandedSumIndex: number;
  utanSummeringSumColCount?: number;
}

export interface IRadbaseradVariables {
  rows: IRadBaseradRow[];
  isEmpty: boolean;
  showComparisonNumber: boolean;
  // scrollParentRef: any;
  handleScroll: (event: React.UIEvent) => void;
}

export interface IColumn {
  title1: string;
  title2: string;
  title3: string;
  fieldInfo: IFieldInfo[];
  fieldType: string;
  visible?: boolean;
  inverted?: boolean;
  isPercent?: boolean;
  forceCalculation?: boolean | null;
}

const styles = (theme: Theme) => ({
  detailInfoContainer: {
    paddingTop: '8px', // EkonomiskAnalysDetailInfo används normalt i en tablecell, som har 8px paddingTop...
    paddingBottom: '5px',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: theme.customProperties.EkonomiskAnalysTableBorderColor,
  },
});

const EkonomiskAnalysTableBase: React.FunctionComponent<
  React.PropsWithChildren<IInnerProps>
> = props => {
  const { userInfo } = useContext(EkonomiskAnalysContext);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    userInfo?.utanSummeringRowsPerPage ?? UNCHOSEN_ROWS_PER_PAGE
  );
  const showDetailInfo =
    !props.showTable ||
    !props.settingsForCurrentlyShown.visaSummeringar ||
    (props.selectedAnalys && props.stopTableRendering);

  const onChangeRowsPerPage = (event: any) => {
    const value = parseInt(event.target.value, 10);
    setPage(0);
    setRowsPerPage(value);
    if (props.saveDBSetting) {
      props.saveDBSetting(
        AnvandarInfo.ekonomiskAnalysUtanSummeringRowsPerPage.name,
        { id: shortid.generate(), value: value.toString() }
      );
    }
  };

  const showRightSideComponent =
    !props.settingsForCurrentlyShown.visaSummeringar &&
    !props.settingsChanged &&
    (props.lopandeVariables?.rows.length ?? 0) > 0;

  return (
    <>
      {showDetailInfo && (
        <div className={props.classes.detailInfoContainer}>
          <EkonomiskAnalysDetailInfo
            impersonatedCodePrivilege={props.impersonatedCodePrivilege}
            analysSettings={props.settingsForCurrentlyShown}
            denomination={props.updatedSettings.other.denomination}
            updatedAnalysSettingsUrval={props.updatedSettings.urval}
            showComparsion={props.updatedSettings.other.showComparison}
            updateImpersonatedCodePrivilege={
              props.updateImpersonatedCodePrivilege
            }
            onDenominationChange={props.onDenominationChanged}
            selectedAnalys={props.selectedAnalys}
            rightSideComponent={
              showRightSideComponent ? (
                <EDTablePagination
                  onChangePage={(
                    event: React.MouseEvent<HTMLButtonElement> | null,
                    page: number
                  ) => {
                    setPage(page);
                  }}
                  onChangeRowsPerPage={onChangeRowsPerPage}
                  page={page}
                  rowCount={props.lopandeVariables?.rows.length ?? 0}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={ROWS_PER_PAGE_CHOICES}
                />
              ) : undefined
            }
            sammanfattningsMode={props.sammanfattningsMode}
          />
        </div>
      )}
      {props.showErrorPage && (
        <EkonomiskAnalysDataErrorPage errorText="Ingen data hittad i analysen" />
      )}
      {props.showTable && (
        <EkonomiskAnalysTable
          columns={props.columns}
          lopandeVariables={props.lopandeVariables}
          radbaseradVariables={props.radbaseradVariables}
          showComparsion={props.updatedSettings.other.showComparison}
          impersonatedCodePrivilege={props.impersonatedCodePrivilege}
          updateImpersonatedCodePrivilege={
            props.updateImpersonatedCodePrivilege
          }
          settingsForCurrentlyShown={props.settingsForCurrentlyShown}
          updatedSettings={props.updatedSettings}
          selectedAnalys={props.selectedAnalys}
          onDenominationChange={props.onDenominationChanged}
          page={page}
          rowsPerPage={rowsPerPage}
          sammanfattningsMode={props.sammanfattningsMode}
        />
      )}
    </>
  );
};

const EnhancedEkonomiskAnalysTableBase: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { userInfo, harPeriodiseradBudget } = useContext(
    EkonomiskAnalysContext
  );
  if (props.userInfoLoading) {
    return <EDAvenyProgress />;
  }

  return (
    <>
      {props.lopandeAnalys && (
        <EkonomiskAnalysQuery
          name={props.settingsForCurrentlyShown.name}
          title={props.settingsForCurrentlyShown.title}
          category={props.settingsForCurrentlyShown.category}
          columns={props.settingsForCurrentlyShown.columns.map(x => x.data)}
          sums={props.settingsForCurrentlyShown.sums}
          dates={props.settingsForCurrentlyShown.dates}
          visaSummeringar={props.settingsForCurrentlyShown.visaSummeringar}
          codePrivilegesId={
            props.impersonatedCodePrivilege
              ? props.impersonatedCodePrivilege.id
              : props.selectedAnalys.codePrivilege.id
          }
          baseUrvalId={
            props.selectedAnalys.analys.grundurval
              ? props.selectedAnalys.analys.grundurval.id
              : undefined
          }
          urval={tryConvertUIUrvalToTEDUrval(props.updatedSettings.urval)}
          endastBokfort={
            !props.settingsForCurrentlyShown.other.includeEjBokfordaJournaler
          }
          forceHelarsjamforelse={getForceHelarsJamforelse(
            harPeriodiseradBudget,
            userInfo?.preferHelarsjamforelse
          )}
          detailedSelection={tryConvertUIUrvalToTEDUrval(
            props.settingsForCurrentlyShown.detaljUrval
          )}
          settingsChanged={props.settingsChanged}
          setRefetchAnalys={props.setRefetchAnalys}
          skip={props.stopTableRendering}
        >
          {(data, hideTable = false) => {
            const showTable =
              !props.settingsChanged &&
              data.data.length > 0 &&
              !hideTable &&
              !props.stopTableRendering;
            return (
              <EkonomiskAnalysSaveDBSettingMutation>
                {saveDBSetting => (
                  <EkonomiskAnalysTableBase
                    showTable={showTable}
                    showErrorPage={data.data.length === 0 && !hideTable}
                    columns={data.columns}
                    lopandeVariables={{
                      rows: data.data,
                      totalSumRow: data.totalSum,
                      expandedSumIndex: props.expandedSumIndex,
                      utanSummeringSumColCount: data.summeringColCount,
                    }}
                    saveDBSetting={saveDBSetting}
                    {...props}
                  />
                )}
              </EkonomiskAnalysSaveDBSettingMutation>
            );
          }}
        </EkonomiskAnalysQuery>
      )}
      {!props.lopandeAnalys && (
        <EkonomiskAnalysRadBaseradQuery
          id={props.selectedAnalys.analys.id}
          dates={props.settingsForCurrentlyShown.dates}
          codePrivilegesId={
            props.impersonatedCodePrivilege
              ? props.impersonatedCodePrivilege.id
              : props.selectedAnalys.codePrivilege.id
          }
          endastBokfort={
            !props.settingsForCurrentlyShown.other.includeEjBokfordaJournaler
          }
          forceHelarsjamforelse={getForceHelarsJamforelse(
            harPeriodiseradBudget,
            userInfo?.preferHelarsjamforelse
          )}
          userUrval={props.settingsForCurrentlyShown.urval}
          settingsChanged={props.settingsChanged}
          setRefetchAnalys={props.setRefetchAnalys}
          grundurvalId={
            props.selectedAnalys.analys.grundurval
              ? props.selectedAnalys.analys.grundurval.id
              : undefined
          }
          skip={props.stopTableRendering}
        >
          {(analys, hideTable = false) => {
            const showTable =
              !props.settingsChanged &&
              !analys.isEmpty &&
              !hideTable &&
              !props.stopTableRendering;
            return (
              <EkonomiskAnalysTableBase
                showTable={showTable}
                showErrorPage={analys.isEmpty && !hideTable}
                columns={analys.columns}
                radbaseradVariables={{
                  isEmpty: analys.isEmpty,
                  rows: analys.rows,
                  // scrollParentRef: props.scrollParentRef,
                  handleScroll: props.handleScroll,
                  showComparisonNumber:
                    props.updatedSettings.other.showComparison,
                }}
                {...props}
              />
            );
          }}
        </EkonomiskAnalysRadBaseradQuery>
      )}
    </>
  );
};

export default withStyles(EnhancedEkonomiskAnalysTableBase, styles);
